"use client";

import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import "./ChannelCard.css";

const ChannelCard = ({ channel, onJoin, onCancel, onView }) => {
  const { t, i18n } = useTranslation();
  const { getUserType } = useAuth();
  
  // Get current user type color
  const currentUserType = getUserType();
  const userTypeColor = getUserTypeColor(currentUserType);

  const handleCardClick = (e) => {
    // Don't navigate if clicking action buttons
    if (e.target.closest('.channel-card-join-btn, .channel-card-cancel-btn')) {
      return;
    }
    onView();
  };

  const handleJoinClick = (e) => {
    e.stopPropagation();
    if (onJoin) onJoin();
  };

  const handleCancelClick = (e) => {
    e.stopPropagation();
    if (onCancel) onCancel();
  };

  const memberCount = channel.memberCount ?? 0;
  const postCount = channel.postCount ?? 0;
  const formattedMemberCount = memberCount.toLocaleString(i18n.language);
  const formattedPostCount = postCount.toLocaleString(i18n.language);
  const membersLabel = t("channelCard.stats.members", {
    count: memberCount,
    formatted: formattedMemberCount,
  });
  const postsLabel = t("channelCard.stats.posts", {
    count: postCount,
    formatted: formattedPostCount,
  });

  return (
    <div className="channel-card" onClick={handleCardClick} style={{ '--user-type-color': userTypeColor }}>
      {!channel.isPublic && (
        <div className="channel-card-private-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          {t("channelCard.badges.private")}
        </div>
      )}
      <div className="channel-card-content">
        <div className="channel-card-header">
          <div className="channel-card-profile-image">
            <img
              src={channel.profileImage || "/images/social/pic-placeholder.png"}
              alt={channel.name}
              onError={(e) => {
                e.target.src = "/images/social/pic-placeholder.png";
              }}
            />
          </div>
        </div>
        <h3 className="channel-card-name">{channel.name}</h3>
        <p className="channel-card-description">{channel.description}</p>
        <div className="channel-card-stats">
          <div className="channel-card-stat">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <span>{membersLabel}</span>
          </div>
          <div className="channel-card-stat">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            <span>{postsLabel}</span>
          </div>
        </div>
        <div className="channel-card-owner">
          {/* <span>{t("channelCard.owner", { name: channel.owner.name })}</span> */}
        {(channel.isJoined || channel.isPublic) && (
          <div className="channel-card-action">
            {t("common.view", "View")}
          </div>
        )}
        </div>
        {(() => {
          const joinRequestStatus = channel.joinRequestStatus || (channel.isJoined ? 'joined' : 'none');

          if (joinRequestStatus === 'joined') {
            return null; // Don't show leave button on the card list
          } else if (joinRequestStatus === 'pending') {
            return (
              <div className="channel-card-pending-actions">
                <span className="channel-card-pending-text">
                  {t("channelCard.actions.requestSent", { defaultValue: "Join request sent" })}
                </span>
                <button className="channel-card-cancel-btn" onClick={handleCancelClick}>
                  {t("channelCard.actions.cancel", { defaultValue: "Cancel" })}
                </button>
              </div>
            );
          } else {
            // none - show join button
            return (
              <button className="channel-card-join-btn" onClick={handleJoinClick}>
                {channel.isPublic ? t("channelCard.actions.join") : t("channelCard.actions.request")}
              </button>
            );
          }
        })()}
      </div>
    </div>
  );
};

export default ChannelCard;

